import {
  ExtractedFirmQuoteDTO,
  useApiTransportPricesV1FirmQuotesBulkCreateCreate,
  useApiTransportPricesV1FirmQuotesExtractFirmQuotesCreate,
  useApiTransportPricesV1FirmQuotesUploadWhatsappDumpCreate,
  useUploadFile,
} from 'api';
import { CarrierAutocomplete, Form, useForm } from 'form';
import { useTranslation } from 'i18n';
import { addError } from 'monitor';
import { type ChangeEvent, useState } from 'react';
import { Box, Button, FilePicker, Page, Section } from 'ui';

import { FirmQuoteBulkForm } from '../../components/FirmQuoteBulkForm/FirmQuoteBulkForm';

export const FirmQuoteBulkCreate = () => {
  const { t } = useTranslation('orders');
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isExtracting, setIsExtracting] = useState<boolean>(false);
  const form = useForm();
  const [filePath, setFilePath] = useState<string | null>();
  const [quotes, setQuotes] = useState<ExtractedFirmQuoteDTO[]>([]);
  const [selectedQuotes, setSelectedQuotes] = useState<ExtractedFirmQuoteDTO[]>([]);
  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutateAsync: generateUploadUrl } = useApiTransportPricesV1FirmQuotesUploadWhatsappDumpCreate();
  const { mutateAsync: extractFirmQuotes } = useApiTransportPricesV1FirmQuotesExtractFirmQuotesCreate();
  const { mutateAsync: bulkCreateFirmQuotes, isPending: isCreatingQuotes } =
    useApiTransportPricesV1FirmQuotesBulkCreateCreate();

  const handleUploadWhatsAppDump = async (event: ChangeEvent<HTMLInputElement>) => {
    setIsUploading(true);
    const files = event?.target?.files;
    if (!files?.length) {
      addError(Error(t('transportCostsFirmQuoteBulkCreate.errors.noFileSelected')));
      return;
    }

    setIsFileUploaded(true);
    try {
      const file = Array.from(files).at(-1);
      if (!file) {
        addError(Error(t('transportCostsFirmQuoteBulkCreate.errors.noFileSelected')));
        return;
      }
      const { upload_url, file_path } = await generateUploadUrl({ data: { content_type: file.type } });
      await uploadFile({
        file,
        headers: { 'content-type': file.type },
        url: upload_url,
      });
      setFilePath(file_path);
      setIsUploading(false);
    } catch (error) {
      setIsUploading(false);
      addError(error);
    }
  };
  const handleExtractFirmQuote = async () => {
    if (!filePath) {
      addError(Error(t('transportCostsFirmQuoteBulkCreate.errors.noFileSelected')));
      return;
    }
    setIsExtracting(true);
    const formValues = form.getValues();
    const quotes = await extractFirmQuotes({
      data: {
        forwarder_id: formValues.forwarder_id,
        whatsapp_dump_path: filePath,
      },
    });
    setQuotes(quotes);
    setIsExtracting(false);
  };

  const handleBulkCreateFirmQuotes = async () => {
    if (!selectedQuotes.length) {
      addError(Error(t('transportCostsFirmQuoteBulkCreate.errors.selectAtLeastOneQuote')));
      return;
    }

    try {
      const firmQuotesToCreate = selectedQuotes.map((quote) => ({
        ...quote,
        local_destination_costs: quote.local_destination_costs || [],
        local_origin_costs: quote.local_origin_costs || [],
      }));

      await bulkCreateFirmQuotes({
        data: {
          firm_quotes: firmQuotesToCreate,
        },
      });

      setSelectedQuotes([]);
    } catch (error) {
      addError(error);
    }
  };

  const processRowUpdate = (newQuote: ExtractedFirmQuoteDTO) => {
    const updatedQuote = {
      ...newQuote,
      direct_land_cost: newQuote.direct_land_cost || null,
      direct_land_currency: newQuote.direct_land_currency || null,
      ocean_freight_cost: newQuote.ocean_freight_cost || null,
      ocean_freight_currency: newQuote.ocean_freight_currency || null,
    };
    setQuotes(quotes.map((quote) => (quote.index === updatedQuote.index ? updatedQuote : quote)));
    return updatedQuote;
  };

  return (
    <Page>
      <Page.Title title={t('transportCostsFirmQuoteBulkCreate.pageTitle')} />
      <Page.Divider />
      <Section>
        <Form {...form}>
          <FilePicker
            multiple
            onChange={handleUploadWhatsAppDump}
            label={t('transportCostsFirmQuoteBulkCreate.form.labels.whatsappDumpFile')}
            buttonProps={{ fullWidth: true, loading: isUploading }}
          />
          <Box sx={{ display: 'flex' }}>
            <CarrierAutocomplete
              name="forwarder_id"
              label={t('transportCostsFirmQuoteBulkCreate.form.labels.freightForwarder')}
            />
            <Box sx={{ display: 'flex', ml: 2 }}>
              <Button disabled={!isFileUploaded} onClick={handleExtractFirmQuote} loading={isExtracting}>
                {t('transportCostsFirmQuoteBulkCreate.form.actions.extractQuotes')}
              </Button>
            </Box>
          </Box>
        </Form>
        <Box sx={{ mt: 2 }}>
          <FirmQuoteBulkForm rows={quotes} onSelectionChange={setSelectedQuotes} processRowUpdate={processRowUpdate} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              disabled={!isFileUploaded || isExtracting || selectedQuotes.length === 0}
              size="medium"
              onClick={handleBulkCreateFirmQuotes}
              loading={isCreatingQuotes}
            >
              {t('transportCostsFirmQuoteBulkCreate.form.actions.bulkCreateFirmQuotes')}
            </Button>
          </Box>
        </Box>
      </Section>
    </Page>
  );
};
